// Thin wrappers around Drive v3 endpoints used by the drawOla adapter.
//
// Scope: `drive.file` — we can only see/modify files we created or that the
// user picks via a Google-hosted picker (not used here). Every file we own is
// tagged with `appProperties.drawOlaApp = "1"` and lives under a top-level
// "drawOla" folder for user visibility.

import {
  ConflictError,
  NetworkError,
  NotFoundError,
  TokenExpiredError,
} from "../../errors";

import {
  APP_PROPERTY_KEY,
  APP_PROPERTY_VALUE,
  DRIVE_API,
  DRIVE_UPLOAD_API,
  FOLDER_NAME,
  FOLDER_STORAGE_KEY,
  GOOGLE_FOLDER_MIME,
  MIME_TYPE,
} from "./constants";

import type { DriveAuth } from "./auth";

export type DriveFile = {
  id: string;
  name: string;
  modifiedTime: string;
  version: string;
  size?: string;
};

type ListFilesResponse = { files?: DriveFile[] };

export type DriveClient = {
  ensureFolder(): Promise<string>;
  list(): Promise<DriveFile[]>;
  getMetadata(id: string): Promise<DriveFile>;
  getContent(id: string): Promise<unknown>;
  create(args: {
    name: string;
    envelope: unknown;
  }): Promise<DriveFile>;
  update(args: {
    id: string;
    name: string;
    envelope: unknown;
    ifMatchVersion?: string;
  }): Promise<DriveFile>;
  remove(id: string): Promise<void>;
  rename(id: string, newName: string): Promise<DriveFile>;
};

/** Creates a client for the Google Drive file endpoints. */
export function createDriveClient(auth: DriveAuth): DriveClient {
  let folderId: string | null = null;
  try {
    folderId = sessionStorage.getItem(FOLDER_STORAGE_KEY);
  } catch {
    // ignore
  }

  /** Fetch wrapper: inject bearer token, retry once on 401 after silent
   *  refresh, translate network / HTTP errors into our error classes. */
  const authedFetch = async (
    url: string,
    init: RequestInit = {},
    retried = false,
  ): Promise<Response> => {
    let token: string;
    try {
      token = await auth.getAccessToken();
    } catch (error) {
      if (error instanceof NetworkError) {
        throw error;
      }
      throw new TokenExpiredError();
    }
    const headers = new Headers(init.headers ?? {});
    headers.set("Authorization", `Bearer ${token}`);
    let res: Response;
    try {
      res = await fetch(url, { ...init, headers });
    } catch (e) {
      throw new NetworkError(e);
    }
    if (res.status === 401 && !retried) {
      try {
        await auth.getAccessToken({ forceRefresh: true });
      } catch (error) {
        if (error instanceof NetworkError) {
          throw error;
        }
        throw new TokenExpiredError();
      }
      return authedFetch(url, init, true);
    }
    return res;
  };

  const parseOrThrow = async <T>(res: Response): Promise<T> => {
    if (res.status === 401) {
      throw new TokenExpiredError();
    }
    if (res.status === 404) {
      throw new NotFoundError();
    }
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`Drive ${res.status}: ${body}`);
    }
    return (await res.json()) as T;
  };

  const cacheFolder = (id: string) => {
    folderId = id;
    try {
      sessionStorage.setItem(FOLDER_STORAGE_KEY, id);
    } catch {
      // ignore
    }
  };

  const ensureFolder = async (): Promise<string> => {
    if (folderId) {
      return folderId;
    }
    const q = [
      `name='${FOLDER_NAME}'`,
      `mimeType='${GOOGLE_FOLDER_MIME}'`,
      "trashed=false",
    ].join(" and ");
    const listUrl = `${DRIVE_API}/files?q=${encodeURIComponent(q)}&fields=files(id,name)&spaces=drive`;
    const list = await parseOrThrow<ListFilesResponse>(
      await authedFetch(listUrl),
    );
    // Drive matches `name=` case-insensitively, so filter in JS for exact case.
    const exact = list.files?.find((f) => f.name === FOLDER_NAME);
    if (exact) {
      cacheFolder(exact.id);
      return folderId!;
    }
    const create = await parseOrThrow<DriveFile>(
      await authedFetch(`${DRIVE_API}/files?fields=id`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: FOLDER_NAME,
          mimeType: GOOGLE_FOLDER_MIME,
        }),
      }),
    );
    cacheFolder(create.id);
    return folderId!;
  };

  const list = async (): Promise<DriveFile[]> => {
    const q = [
      `appProperties has { key='${APP_PROPERTY_KEY}' and value='${APP_PROPERTY_VALUE}' }`,
      "trashed=false",
    ].join(" and ");
    const url =
      `${DRIVE_API}/files?q=${encodeURIComponent(q)}` +
      `&fields=files(id,name,modifiedTime,size,version)` +
      `&orderBy=${encodeURIComponent("modifiedTime desc")}&pageSize=100`;
    const res = await parseOrThrow<ListFilesResponse>(await authedFetch(url));
    return res.files ?? [];
  };

  const getMetadata = async (id: string): Promise<DriveFile> => {
    const url = `${DRIVE_API}/files/${encodeURIComponent(id)}?fields=id,name,modifiedTime,size,version`;
    return parseOrThrow<DriveFile>(await authedFetch(url));
  };

  const getContent = async (id: string): Promise<unknown> => {
    const url = `${DRIVE_API}/files/${encodeURIComponent(id)}?alt=media`;
    const res = await authedFetch(url);
    if (res.status === 404) {
      throw new NotFoundError();
    }
    if (res.status === 401) {
      throw new TokenExpiredError();
    }
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`Drive read ${res.status}: ${body}`);
    }
    return res.json();
  };

  const buildMultipart = (metadata: object, envelope: unknown) => {
    const boundary = `drawOla-${Math.random().toString(36).slice(2)}`;
    const body =
      `--${boundary}\r\n` +
      `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
      `${JSON.stringify(metadata)}\r\n` +
      `--${boundary}\r\n` +
      `Content-Type: ${MIME_TYPE}\r\n\r\n` +
      `${JSON.stringify(envelope)}\r\n` +
      `--${boundary}--`;
    return {
      body,
      contentType: `multipart/related; boundary=${boundary}`,
    };
  };

  const create = async (args: {
    name: string;
    envelope: unknown;
  }): Promise<DriveFile> => {
    const parentId = await ensureFolder();
    const metadata = {
      name: args.name,
      mimeType: MIME_TYPE,
      parents: [parentId],
      appProperties: { [APP_PROPERTY_KEY]: APP_PROPERTY_VALUE },
    };
    const { body, contentType } = buildMultipart(metadata, args.envelope);
    const url = `${DRIVE_UPLOAD_API}/files?uploadType=multipart&fields=id,name,modifiedTime,size,version`;
    return parseOrThrow<DriveFile>(
      await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": contentType },
        body,
      }),
    );
  };

  const update = async (args: {
    id: string;
    name: string;
    envelope: unknown;
    ifMatchVersion?: string;
  }): Promise<DriveFile> => {
    if (args.ifMatchVersion) {
      // Drive v3's If-Match works on ETag, not our opaque version. Do an
      // explicit pre-read: if remote version differs, throw ConflictError.
      const remote = await getMetadata(args.id);
      if (remote.version !== args.ifMatchVersion) {
        throw new ConflictError(remote.version);
      }
    }
    // Step 1: rename via metadata PATCH (only if name differs — cheap enough
    // to always issue; Drive is idempotent).
    const meta = await parseOrThrow<DriveFile>(
      await authedFetch(
        `${DRIVE_API}/files/${encodeURIComponent(args.id)}?fields=id,name,modifiedTime,size,version`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: args.name }),
        },
      ),
    );
    // Step 2: body PATCH via uploadType=media.
    const url = `${DRIVE_UPLOAD_API}/files/${encodeURIComponent(args.id)}?uploadType=media&fields=id,name,modifiedTime,size,version`;
    const res = await authedFetch(url, {
      method: "PATCH",
      headers: { "Content-Type": MIME_TYPE },
      body: JSON.stringify(args.envelope),
    });
    if (res.status === 412) {
      // If a future iteration switches to If-Match, surface that here.
      const remote = await getMetadata(args.id).catch(() => meta);
      throw new ConflictError(remote.version);
    }
    return parseOrThrow<DriveFile>(res);
  };

  const remove = async (id: string): Promise<void> => {
    const res = await authedFetch(
      `${DRIVE_API}/files/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
    if (res.status === 404) {
      throw new NotFoundError();
    }
    if (res.status === 401) {
      throw new TokenExpiredError();
    }
    if (!res.ok && res.status !== 204) {
      const body = await res.text().catch(() => "");
      throw new Error(`Drive delete ${res.status}: ${body}`);
    }
  };

  const rename = async (id: string, newName: string): Promise<DriveFile> => {
    const url = `${DRIVE_API}/files/${encodeURIComponent(id)}?fields=id,name,modifiedTime,size,version`;
    return parseOrThrow<DriveFile>(
      await authedFetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName }),
      }),
    );
  };

  return {
    ensureFolder,
    list,
    getMetadata,
    getContent,
    create,
    update,
    remove,
    rename,
  };
}
